import { useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { barY, plot, ruleY } from '@observablehq/plot';
import classnames from 'classnames/bind';
import { useResizeObserver } from 'app/lib/hooks';
import { LoadingSize } from 'app/components/core/Loading';
import { Spinner } from 'app/components/core/Spinner';
import { Banner, BannerVariant } from 'app/components/core/Banner';
import * as styles from './WarehouseLoadDialog.css';

const cx = classnames.bind(styles);

const tickFormatX = (date) =>
    date.toLocaleTimeString('en', {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
    });

export function WarehouseLoadChart({ isOverloaded, warehouse, data }) {
    const { t } = useTranslation('components', { keyPrefix: 'WarehouseLoadDialog.chart' });
    const ref = useRef(null);
    const { width, height } = useResizeObserver(ref);
    const classes = cx('content-pane', { 'is-overloaded': isOverloaded });

    useEffect(() => {
        const bars = [];
        let domainMax = 10;

        if (!data) {
            return;
        }

        for (const { date, queued, running } of data) {
            domainMax = Math.max(domainMax, queued + running);
            bars.push(
                { date, state: 'Running', queries: running },
                { date, state: 'Queued', queries: queued }
            );
        }

        const chart = plot({
            title: t('title'),
            width: Math.max(512, width - 34),
            height: Math.max(128, height - 70),
            marginTop: 32,
            marginRight: 8,
            marginBottom: 24,
            marginLeft: 32,
            color: { legend: true, scheme: 'RdBu' },
            x: {
                type: 'band',
                label: null,
                tickFormat: tickFormatX
            },
            y: { grid: true, domain: [0, domainMax] },
            marks: [barY(bars, { x: 'date', y: 'queries', fill: 'state' }), ruleY([0])]
        });

        ref.current.append(chart);

        return () => {
            chart.remove();
        };
    }, [t, width, height, data]);

    return (
        <section className={classes}>
            {isOverloaded && (
                <Banner variant={BannerVariant.ERROR}>
                    <Trans
                        t={t}
                        i18nKey="warning"
                        components={{ b: <strong /> }}
                        values={{ warehouse }}
                    />
                </Banner>
            )}
            <div className={cx('chart')} ref={ref}>
                {!data && (
                    <Spinner className={cx('empty')} size={LoadingSize.LARGE}>
                        <Trans
                            t={t}
                            i18nKey="empty"
                            components={{ h: <h4 />, p: <p /> }}
                            values={{ warehouse }}
                        />
                    </Spinner>
                )}
            </div>
        </section>
    );
}
